// A copy of the current session on the device, so the queue and the lyrics are there when
// the network is not: the session's current song and verse, whether this device is the host,
// a host's change that still has to reach the server, and the queued songs with their
// verses. One session is kept at a time.

const PREFIX = "session:";
const MAX_CHARS = 1_500_000; // well below the usual 5 MB local storage quota
const VERSION = 2; // 1: selection and songs; 2: plus the host's pending selection

const keyFor = (code) => `${PREFIX}${code}`;

const pendingOf = (session) => {
  const pending = session?.isHost === true ? session?.pendingSelection : null;
  if (!pending || typeof pending !== "object") return null;
  const SongId = pending.SongId ?? null;
  const VerseNum = pending.VerseNum ?? -1;
  if (SongId !== null && (!Number.isSafeInteger(SongId) || SongId <= 0)) return null;
  if (!Number.isSafeInteger(VerseNum) || VerseNum < -1 || (SongId === null && VerseNum !== -1)) return null;
  return { SongId, VerseNum };
};

/** Saves the session; returns false when it could not be stored (no storage, too large). */
export function saveSessionCache(code, { session, songs }) {
  if (!code) return false;
  const entry = {
    version: VERSION,
    savedAt: Date.now(),
    session: {
      SongId: session?.SongId ?? null,
      VerseNum: session?.VerseNum ?? -1,
      isHost: session?.isHost === true,
      pendingSelection: pendingOf(session),
    },
    songs: Array.isArray(songs) ? songs : Object.values(songs || {}),
  };

  try {
    const json = JSON.stringify(entry);
    if (json.length > MAX_CHARS) return false;
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(PREFIX) && key !== keyFor(code)) localStorage.removeItem(key);
    }
    localStorage.setItem(keyFor(code), json);
    return true;
  } catch {
    return false;
  }
}

/** The cached session, or null when there is none (or it is unreadable). Entries written before version 2 load without a pending selection. */
export function loadSessionCache(code) {
  if (!code) return null;
  try {
    const raw = localStorage.getItem(keyFor(code));
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (!entry || !Array.isArray(entry.songs) || !entry.session) return null;
    if (entry.version != null && entry.version !== 1 && entry.version !== VERSION) return null;
    return { ...entry, session: { ...entry.session, pendingSelection: pendingOf(entry.session) } };
  } catch {
    return null;
  }
}

export const hasSessionCache = (code) => loadSessionCache(code) !== null;

export function clearSessionCache(code) {
  try {
    if (code) {
      localStorage.removeItem(keyFor(code));
      return;
    }
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(PREFIX)) localStorage.removeItem(key);
    }
  } catch {
    // nothing to clear
  }
}
